import React, { useState, useEffect } from 'react';
import { ChartLayout, OverviewContent, MethodologyContent, RelevanceContent, EquityContent } from '../../../components/charts/ChartLayout';
import { KeyMetrics, KeyMetric } from '../../../components/charts/KeyMetrics';
import { pumaAggregationService, PumaAggregation } from '../services/pumaAggregationService';
import { simplePumsService, FullPumaRecord } from '../services/simplePumsService';
import { PumaResult } from '../services/pumsTypes';
import { getPumaName, getCountyFromPuma } from '../services/pumaNames';
import { getRegionName, getPumaCodesForRegion } from '../services/regionMapping';
import BayAreaCountyMap from './BayAreaCountyMap';
import RegionSelector from './RegionSelector';

interface FilterState {
  // Multifamily unit size filters
  unitsUnder5: boolean;
  units5to19: boolean;
  units20to50: boolean;
  units50Plus: boolean;
  
  // Vintage filters
  vintagePre1980: boolean;
  vintage1980to2000: boolean;
  vintage2000Plus: boolean;
  
  // Heating fuel filters
  heatingElectric: boolean;
  heatingGas: boolean;
  heatingOther: boolean;
  
  // Tenure filters
  ownerOccupied: boolean;
  renterOccupied: boolean;
  
  // Rent burden filters
  lowRentBurden: boolean; // < 30%
  highRentBurden: boolean; // >= 30%
  
  // Energy burden filters
  lowEnergyBurden: boolean; // < 6%
  highEnergyBurden: boolean; // >= 6%
  
  // Demographic filters (householder only)
  seniorHouseholder: boolean; // 65+
  pocHouseholder: boolean; // Person of Color
  lowEducation: boolean; // Less than college degree
}

interface FilteredResult {
  puma: number;
  pumaName: string;
  county: string;
  totalHouseholds: number;
  filteredCount: number;
  percentage: number;
}

interface BayRENMultifamilyTargetingToolProps {
  onMetricsUpdate?: (metrics: any) => void;
}

const BayRENMultifamilyTargetingTool: React.FC<BayRENMultifamilyTargetingToolProps> = ({ onMetricsUpdate }) => {
  const [rawData, setRawData] = useState<FullPumaRecord[]>([]);
  const [filteredResults, setFilteredResults] = useState<FilteredResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');
  const [selectedRegion, setSelectedRegion] = useState<number>(3); // Default to San Francisco Bay Area
  
  // Check if the selected region is currently supported
  const isRegionSupported = (regionId: number): boolean => {
    const regionPumaCodes = getPumaCodesForRegion(regionId);
    return regionPumaCodes.length > 0; // Supported if we have PUMA codes defined
  };
  
  // Handle region change
  const handleRegionChange = (regionId: number) => {
    setSelectedRegion(regionId);
    // Data loading and reset will be handled by the useEffect
  };
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    // Default: show all multifamily (20+ units)
    unitsUnder5: false,
    units5to19: false,
    units20to50: true,
    units50Plus: true,
    
    // Default: all vintages
    vintagePre1980: true,
    vintage1980to2000: true,
    vintage2000Plus: true,
    
    // Default: all heating fuels
    heatingElectric: true,
    heatingGas: true,
    heatingOther: true,
    
    // Default: all tenure
    ownerOccupied: true,
    renterOccupied: true,
    
    // Default: all rent burden levels
    lowRentBurden: true,
    highRentBurden: true,
    
    // Default: all energy burden levels
    lowEnergyBurden: true,
    highEnergyBurden: true,
    
    // Default: all demographics
    seniorHouseholder: true,
    pocHouseholder: true,
    lowEducation: true,
  });

  // Load raw PUMS data
  useEffect(() => {
    const loadData = async () => {
      // Only load data for supported regions
      if (!isRegionSupported(selectedRegion)) {
        setRawData([]);
        setFilteredResults([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading raw PUMS data for filtering...');
        const data = await simplePumsService.getAllPumaData();
        setRawData(data);
        console.log(`Loaded ${data.length} PUMS records`);
      } catch (err) {
        console.error('Error loading PUMS data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedRegion]); // Trigger when region changes

  // Apply filters and calculate results
  useEffect(() => {
    if (rawData.length === 0) return;

    console.log('Applying filters to PUMS data...');
    
    // Get PUMA codes for the selected region
    const regionPumaCodes = getPumaCodesForRegion(selectedRegion);
    console.log(`Filtering for region ${selectedRegion} with ${regionPumaCodes.length} PUMAs`);
    console.log('Region PUMA codes:', regionPumaCodes.slice(0, 10)); // Show first 10
    
    // Debug: Check what PUMA codes are actually in the raw data
    const allPumaCodesInData = Array.from(new Set(rawData.map(r => r.puma))).sort((a, b) => a - b);
    console.log(`Raw data contains ${allPumaCodesInData.length} unique PUMA codes`);
    console.log('Sample PUMA codes in data:', allPumaCodesInData.slice(0, 20));
    
    // Check overlap between region codes and data codes
    const matchingCodes = regionPumaCodes.filter(code => allPumaCodesInData.includes(code));
    console.log(`Matching PUMA codes: ${matchingCodes.length}/${regionPumaCodes.length}`);
    if (matchingCodes.length > 0) {
      console.log('Matching codes:', matchingCodes.slice(0, 10));
    }
    
    // Group data by PUMA, but only for PUMAs in the selected region
    const pumaMap = new Map<number, FullPumaRecord[]>();
    rawData.forEach(record => {
      // Only include PUMAs that belong to the selected region
      if (regionPumaCodes.length === 0 || regionPumaCodes.includes(record.puma)) {
        if (!pumaMap.has(record.puma)) {
          pumaMap.set(record.puma, []);
        }
        pumaMap.get(record.puma)!.push(record);
      }
    });

    const results: FilteredResult[] = [];

    pumaMap.forEach((records, puma) => {
      let totalWeight = 0;
      let filteredWeight = 0;

      records.forEach(record => {
        const weight = record.wgtp || 0;
        totalWeight += weight;

        // Check if record meets all selected criteria
        if (meetsFilterCriteria(record, filters)) {
          filteredWeight += weight;
        }
      });

      if (totalWeight > 0) {
        results.push({
          puma,
          pumaName: getPumaNameLocal(puma),
          county: getCountyFromPumaLocal(puma),
          totalHouseholds: Math.round(totalWeight),
          filteredCount: Math.round(filteredWeight),
          percentage: totalWeight > 0 ? (filteredWeight / totalWeight) * 100 : 0
        });
      }
    });

    // Sort by filtered count descending
    results.sort((a, b) => b.filteredCount - a.filteredCount);
    
    setFilteredResults(results);
    console.log(`Applied filters: ${results.length} PUMAs with results`);
  }, [rawData, filters, selectedRegion]);

  // Check if a record meets all selected filter criteria
  const meetsFilterCriteria = (record: FullPumaRecord, filterState: FilterState): boolean => {
    // Multifamily unit size check
    const isMultifamily = 
      (filterState.unitsUnder5 && record.bld >= 1 && record.bld <= 2) ||
      (filterState.units5to19 && record.bld >= 3 && record.bld <= 4) ||
      (filterState.units20to50 && record.bld >= 5 && record.bld <= 6) ||
      (filterState.units50Plus && record.bld >= 7);
    
    if (!isMultifamily) return false;

    // Vintage check
    const vintageMatch = 
      (filterState.vintagePre1980 && record.yrblt < 1980) ||
      (filterState.vintage1980to2000 && record.yrblt >= 1980 && record.yrblt <= 2000) ||
      (filterState.vintage2000Plus && record.yrblt > 2000);
    
    if (!vintageMatch) return false;

    // Heating fuel check
    const heatingMatch = 
      (filterState.heatingElectric && record.hfl === 2) ||
      (filterState.heatingGas && record.hfl === 1) ||
      (filterState.heatingOther && record.hfl >= 3 && record.hfl <= 10);
    
    if (!heatingMatch) return false;

    // Tenure check
    const tenureMatch = 
      (filterState.ownerOccupied && record.ten === 1) ||
      (filterState.renterOccupied && record.ten === 2);
    
    if (!tenureMatch) return false;

    // Rent burden check (only for renters)
    if (record.ten === 2 && record.grpip !== null && record.grpip !== undefined && record.grpip > 0) {
      const rentBurdenMatch = 
        (filterState.lowRentBurden && record.grpip < 30) ||
        (filterState.highRentBurden && record.grpip >= 30);
      
      if (!rentBurdenMatch) return false;
    }

    // Energy burden check
    const energyBurden = calculateEnergyBurden(record);
    if (energyBurden !== null && energyBurden !== undefined && !isNaN(energyBurden)) {
      const energyBurdenMatch = 
        (filterState.lowEnergyBurden && energyBurden < 0.06) ||
        (filterState.highEnergyBurden && energyBurden >= 0.06);
      
      if (!energyBurdenMatch) return false;
    }

    // Demographic check (only for householder - RELSHIPP == 20)
    if (record.relshipp === 20) {
      // Check if any demographic filters are enabled
      const hasDemographicFilters = filterState.seniorHouseholder || filterState.pocHouseholder || filterState.lowEducation;
      
      if (hasDemographicFilters) {
        // If demographic filters are enabled, record must match at least one
        const demographicMatch = 
          (filterState.seniorHouseholder && record.agep >= 65) ||
          (filterState.pocHouseholder && !(record.rac1p === 1 && record.hisp === 1)) ||
          (filterState.lowEducation && record.schl < 21);
        
        if (!demographicMatch) return false;
      }
      // If no demographic filters are enabled, all records pass this check
    }

    return true;
  };

  // Calculate energy burden: (12 × (ELEP + GASP + FULP) / HINCP, adjusted with ADJINC)
  const calculateEnergyBurden = (record: FullPumaRecord): number | null => {
    const elep = record.elep || 0;
    const gasp = record.gasp || 0;
    const fulp = record.fulp || 0;
    const hincp = record.hincp || 0;
    const adjinc = record.adjinc || 0;

    if (hincp <= 0) return null;

    const adjustedIncome = hincp * (adjinc / 1000000);
    if (adjustedIncome <= 0) return null;

    const annualEnergyCost = 12 * (elep + gasp + fulp);
    return annualEnergyCost / adjustedIncome;
  };

  // Helper functions
  const getPumaNameLocal = (pumaCode: number): string => {
    return getPumaName(pumaCode);
  };

  const getCountyFromPumaLocal = (pumaCode: number): string => {
    return getCountyFromPuma(pumaCode);
  };

  // Filter toggle handlers
  const toggleFilter = (filterKey: keyof FilterState) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  // Calculate summary metrics
  const totalTargetHouseholds = filteredResults.reduce((sum, result) => sum + result.filteredCount, 0);
  const totalHouseholds = filteredResults.reduce((sum, result) => sum + result.totalHouseholds, 0);
  const avgPercentage = totalHouseholds > 0 ? (totalTargetHouseholds / totalHouseholds) * 100 : 0;

  // Convert FilteredResult to PumaResult for the map
  const pumaResults: PumaResult[] = filteredResults.map(result => {
    // Calculate average energy burden and rent burden for this PUMA
    const pumaRecords = rawData.filter(record => record.puma === result.puma);
    
    let totalEnergyBurden = 0;
    let totalRentBurden = 0;
    let energyBurdenCount = 0;
    let rentBurdenCount = 0;
    
    pumaRecords.forEach(record => {
      const energyBurden = calculateEnergyBurden(record);
      if (energyBurden !== null && energyBurden !== undefined && !isNaN(energyBurden)) {
        totalEnergyBurden += energyBurden;
        energyBurdenCount++;
      }
      
      if (record.ten === 2 && record.grpip !== null && record.grpip !== undefined && record.grpip > 0) {
        totalRentBurden += record.grpip;
        rentBurdenCount++;
      }
    });
    
    const avgEnergyBurden = energyBurdenCount > 0 ? totalEnergyBurden / energyBurdenCount : 0;
    const avgRentBurden = rentBurdenCount > 0 ? totalRentBurden / rentBurdenCount : 0;
    
    return {
      puma: result.puma,
      pumaName: result.pumaName,
      county: result.county,
      totalHouseholds: result.totalHouseholds,
      targetHouseholds: result.filteredCount,
      targetPercentage: result.percentage,
      avgEnergyBurden: avgEnergyBurden,
      avgRentBurden: avgRentBurden,
    };
  });

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#666'
      }}>
        Loading Bay Area PUMS data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#dc2626',
        textAlign: 'center',
        padding: '20px'
      }}>
        Error loading data: {error}
      </div>
    );
  }

  return (
    <ChartLayout
      title="Multifamily Energy Efficiency Targeting Tool"
      description="Identify and estimate the number of households across California that meet specific demographic, energy burden, and housing characteristics. Use Census PUMA-level data to target energy efficiency programs to the communities and building types that need them most. Filter by multiple criteria—including building type, energy burden, income, age, race, and education—to build precise targeting strategies for program design, resource allocation, and outreach planning."
      chartType="bar"
      layout="vertical"
      activeView={activeView}
      onViewChange={setActiveView}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={filteredResults.length.toString()}
            label="PUMAs with Results"
            color="#1e3a8a"
          />
          <KeyMetric
            value={`${(totalTargetHouseholds / 1000).toFixed(0)}K`}
            label="Target Households"
            color="#059669"
          />
          <KeyMetric
            value={`${avgPercentage.toFixed(1)}%`}
            label="Target Percentage"
            color="#7c3aed"
          />
          <KeyMetric
            value={`${(totalHouseholds / 1000).toFixed(0)}K`}
            label="Total Households"
            color="#f59e0b"
          />
        </KeyMetrics>
      }
      chartContent={
        <div style={{ width: '100%' }}>
          {/* Region Selector */}
          <div style={{ 
            backgroundColor: '#ffffff', 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '2px solid #3b82f6',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <RegionSelector
              selectedRegion={selectedRegion}
              onRegionChange={handleRegionChange}
              availableRegions={[3]} // Only show San Francisco Bay Area
            />
          </div>
          
          {/* Show "Coming Soon" message for unsupported regions */}
          {!isRegionSupported(selectedRegion) ? (
            <div style={{ 
              backgroundColor: '#fef3c7', 
              padding: '40px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '2px solid #f59e0b',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#92400e', marginTop: 0 }}>
                Coming Soon: {getRegionName(selectedRegion)}
              </h3>
              <p style={{ color: '#92400e', fontSize: '16px', margin: 0 }}>
                This region is not yet available in the targeting tool. Check back soon for additional California regions.
              </p>
            </div>
          ) : (
            <>
              {/* Filter Controls */}
              <div style={{ 
                backgroundColor: '#f8fafc', 
                padding: '20px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #e2e8f0'
              }}>
            <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#1e293b' }}>
              Filter Criteria
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {/* Multifamily Unit Size */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Unit Size</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.unitsUnder5}
                    onChange={() => toggleFilter('unitsUnder5')}
                    style={{ marginRight: '6px' }}
                  />
                  Under 5 units
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.units5to19}
                    onChange={() => toggleFilter('units5to19')}
                    style={{ marginRight: '6px' }}
                  />
                  5-19 units
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.units20to50}
                    onChange={() => toggleFilter('units20to50')}
                    style={{ marginRight: '6px' }}
                  />
                  20-50 units
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.units50Plus}
                    onChange={() => toggleFilter('units50Plus')}
                    style={{ marginRight: '6px' }}
                  />
                  50+ units
                </label>
              </div>

              {/* Vintage */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Vintage</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.vintagePre1980}
                    onChange={() => toggleFilter('vintagePre1980')}
                    style={{ marginRight: '6px' }}
                  />
                  Pre-1980
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.vintage1980to2000}
                    onChange={() => toggleFilter('vintage1980to2000')}
                    style={{ marginRight: '6px' }}
                  />
                  1980-2000
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.vintage2000Plus}
                    onChange={() => toggleFilter('vintage2000Plus')}
                    style={{ marginRight: '6px' }}
                  />
                  2000+
                </label>
              </div>

              {/* Heating Fuel */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Heating Fuel</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.heatingElectric}
                    onChange={() => toggleFilter('heatingElectric')}
                    style={{ marginRight: '6px' }}
                  />
                  Electric
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.heatingGas}
                    onChange={() => toggleFilter('heatingGas')}
                    style={{ marginRight: '6px' }}
                  />
                  Natural Gas
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.heatingOther}
                    onChange={() => toggleFilter('heatingOther')}
                    style={{ marginRight: '6px' }}
                  />
                  Other
                </label>
              </div>

              {/* Tenure */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Tenure</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.ownerOccupied}
                    onChange={() => toggleFilter('ownerOccupied')}
                    style={{ marginRight: '6px' }}
                  />
                  Owner Occupied
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.renterOccupied}
                    onChange={() => toggleFilter('renterOccupied')}
                    style={{ marginRight: '6px' }}
                  />
                  Renter Occupied
                </label>
              </div>

              {/* Rent Burden */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Rent Burden</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.lowRentBurden}
                    onChange={() => toggleFilter('lowRentBurden')}
                    style={{ marginRight: '6px' }}
                  />
                  Low (&lt; 30%)
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.highRentBurden}
                    onChange={() => toggleFilter('highRentBurden')}
                    style={{ marginRight: '6px' }}
                  />
                  High (≥ 30%)
                </label>
              </div>

              {/* Energy Burden */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Energy Burden</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.lowEnergyBurden}
                    onChange={() => toggleFilter('lowEnergyBurden')}
                    style={{ marginRight: '6px' }}
                  />
                  Low (&lt; 6%)
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.highEnergyBurden}
                    onChange={() => toggleFilter('highEnergyBurden')}
                    style={{ marginRight: '6px' }}
                  />
                  High (≥ 6%)
                </label>
              </div>

              {/* Demographics */}
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Demographics (Householder)</h5>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.seniorHouseholder}
                    onChange={() => toggleFilter('seniorHouseholder')}
                    style={{ marginRight: '6px' }}
                  />
                  Senior (65+)
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.pocHouseholder}
                    onChange={() => toggleFilter('pocHouseholder')}
                    style={{ marginRight: '6px' }}
                  />
                  Person of Color
                </label>
                <label style={{ display: 'block', marginBottom: '4px' }}>
                  <input 
                    type="checkbox" 
                    checked={filters.lowEducation}
                    onChange={() => toggleFilter('lowEducation')}
                    style={{ marginRight: '6px' }}
                  />
                  Less than College
                </label>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div style={{ 
            backgroundColor: 'white', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              backgroundColor: '#f8fafc', 
              padding: '12px 16px', 
              borderBottom: '1px solid #e2e8f0',
              fontWeight: '600'
            }}>
              PUMA Results ({filteredResults.length} areas)
            </div>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f1f5f9', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>PUMA</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>County</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Target Households</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Total Households</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, index) => (
                    <tr key={result.puma} style={{ 
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                      borderBottom: '1px solid #f1f5f9'
                    }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                        {result.puma} - {result.pumaName}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {result.county}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                        {result.filteredCount.toLocaleString()}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {result.totalHouseholds.toLocaleString()}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {result.percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* PUMA Map */}
          <div style={{ 
            backgroundColor: 'white', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px',
            marginTop: '20px',
            padding: '20px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#1e293b' }}>
              Geographic Distribution of Target Households
            </h4>
            <p style={{ marginBottom: '15px', color: '#64748b', fontSize: '14px' }}>
              PUMA areas are color-coded by the percentage of households meeting your selected criteria. 
              Click on any PUMA area to see detailed information.
            </p>
            <BayAreaCountyMap 
              pumaResults={pumaResults}
              loading={loading}
              selectedRegion={selectedRegion}
            />
          </div>
            </>
          )}
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Multifamily Energy Efficiency Targeting"
          description="This interactive tool estimates the number of households meeting specific demographic, energy, and housing characteristics to support targeted energy efficiency program planning and implementation across California."
        >
          <p>
            <strong>Purpose:</strong> The Multifamily Energy Efficiency Targeting Tool is designed to estimate the number of households 
            that meet certain demographic, energy, and housing characteristics. By combining multiple filters, program administrators 
            can identify target populations for energy efficiency initiatives, understand the scale of potential program participants, 
            and prioritize geographic areas for resource allocation.
          </p>
          <p>
            <strong>What is a Census PUMA?</strong> A Public Use Microdata Area (PUMA) is a Census Bureau geographic unit used for 
            releasing Public Use Microdata Sample (PUMS) data. PUMAs are statistical areas that contain at least 100,000 people and 
            are delineated by state and other stakeholders. In California, PUMAs typically represent portions of counties, groups 
            of counties, or combinations of cities and counties. PUMAs allow for detailed demographic and housing analysis while 
            maintaining respondent privacy, as they are larger than census tracts but smaller than counties. This tool aggregates 
            household-level data to the PUMA level to provide regional targeting insights.
          </p>
          <p>
            <strong>How to Use:</strong> Select your targeting criteria using the checkboxes above. The tool automatically 
            calculates and displays the number of households in each PUMA area that meet ALL selected criteria. 
            Results are sorted by the number of target households (highest first), allowing you to quickly identify 
            high-priority areas for program implementation.
          </p>
          <p>
            <strong>Key Features:</strong> The tool uses Census PUMS data with proper statistical weighting to provide 
            accurate household counts at the PUMA level. You can combine multiple filters to create complex targeting scenarios, 
            such as "multifamily buildings with high energy burden and senior householders." The interactive filtering 
            enables real-time exploration of different targeting strategies and their potential reach.
          </p>
          <p>
            <strong>Targeting Strategy:</strong> Use this tool to identify high-priority areas for energy efficiency programs, 
            understand the scale of potential impact, allocate resources effectively across California regions, and design 
            program outreach strategies that match the demographic and housing characteristics of target communities.
          </p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Energy Efficiency Program Implementation"
          description="This targeting tool supports energy efficiency programs by identifying multifamily housing opportunities across California."
        >
          <div className="methodology-details-simple">
            <h5>Program Design</h5>
            <p>
              Identifies specific PUMA areas with high concentrations of target households, enabling 
              program designers to create geographically-focused programs that maximize impact and efficiency. 
              Understanding the scale of potential participants helps set realistic program goals.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Resource Allocation</h5>
            <p>
              Provides data-driven insights for allocating program resources, staff, and contractor capacity. 
              Areas with high target household counts may warrant additional program support, while 
              smaller areas might be served through regional approaches.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Outreach Strategy</h5>
            <p>
              Enables targeted outreach to property owners and residents in high-priority areas. 
              Understanding demographic characteristics helps tailor messaging and engagement 
              strategies to specific communities and their needs.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations in Program Targeting"
          description="This tool helps ensure energy efficiency programs reach communities that need improvements most."
        >
          <div className="methodology-details-simple">
            <h5>Energy Burden Targeting</h5>
            <p>
              High energy burden households spend a disproportionate amount of income on energy costs, 
              limiting their ability to invest in efficiency improvements. Targeting these households 
              ensures programs reach those who need assistance most.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Demographic Equity</h5>
            <p>
              The demographic filters help ensure programs reach diverse communities, including 
              seniors, people of color, and those with lower educational attainment who may face 
              additional barriers to accessing energy efficiency programs.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Geographic Equity</h5>
            <p>
              PUMA-level analysis ensures programs serve communities across all California regions, 
              not just areas with the highest concentrations of multifamily housing. This promotes 
              equitable access to energy efficiency benefits.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="This analysis uses Census Public Use Microdata Sample (PUMS) data to provide accurate household-level targeting information."
          dataSources={[
            'Census Public Use Microdata Sample (PUMS) - Housing and Person files',
            'Supabase pums_households_bayarea table - Processed PUMS data for California regions'
          ]}
          methodology={[
            'Load complete PUMS dataset for California PUMAs using Census statistical weights',
            'Apply user-selected filters to identify households meeting targeting criteria',
            'Calculate weighted household counts for each PUMA area',
            'Sort results by target household count for program prioritization',
            'Display results in interactive table with percentage calculations',
            'Update results dynamically as filters are modified'
          ]}
        />
      }
      showRelevanceEquity={true}
    />
  );
};

export default BayRENMultifamilyTargetingTool;
